import { useRef } from 'react';
import { FileSpreadsheet, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatFileSize } from '../utils';

interface BulkImportFileInputProps {
  file: File | null;
  error: string;
  disabled?: boolean;
  loading?: boolean;
  onChange: (_file: File | null) => void;
}

export function BulkImportFileInput({
  file,
  error,
  disabled = false,
  loading = false,
  onChange,
}: BulkImportFileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <label htmlFor="bulk-csv-file" className="text-sm font-medium text-gray-700">
        Arquivo CSV
      </label>
      <input
        ref={inputRef}
        id="bulk-csv-file"
        type="file"
        accept=".csv,text/csv"
        className="sr-only"
        disabled={disabled || loading}
        onChange={(event) => {
          const nextFile = event.target.files?.[0] ?? null;
          onChange(nextFile);
          event.target.value = '';
        }}
      />
      {file ? (
        <div className="flex flex-col gap-3 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <FileSpreadsheet className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={openFilePicker} disabled={disabled || loading}>
              Substituir
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onChange(null)}
              disabled={disabled || loading}
              aria-label="Remover arquivo"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openFilePicker}
          disabled={disabled || loading}
          className="flex w-full flex-col items-center justify-center rounded-md border border-dashed border-gray-300 px-4 py-8 text-sm text-muted-foreground hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Processando arquivo...
            </span>
          ) : (
            'Selecionar arquivo CSV'
          )}
        </button>
      )}
      {loading && file && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-live="polite">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Processando arquivo...
        </p>
      )}
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
